/**
 * Typed and formatted values, as the server reads them (server/src/formats/: the authority; `vectors.json` there holds
 * the cases both are tested against). Email, phone and URL fields' values and a text field's `format` (CPF, CNPJ,
 * IBAN, plates…) are stored as written; each one *reads* as a {@link Read}: its key (the canonical form things compare
 * by), how it's shown, whether it's valid and, for some, which kind it is. Phones and plates read in a region: the
 * field's, else the space's. Here for showing and typing them: placeholders, masks, read-backs.
 */

import type { CountryCode } from "libphonenumber-js";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import type { FieldType, TextFormat } from "../api/types.gen.ts";
import { type Key, t as translate } from "../i18n/index.ts";
import { customKey, customOf } from "./pattern.ts";

/**
 * What values are read as: a field type's (email, phone, url), a text format's, or a field's own pattern and mask's
 * (`custom`: its spec checks and shows them, see `pattern.ts`; they compare by their letters and digits).
 */
export type Kind = "email" | "phone" | "url" | TextFormat | "custom";

/** The field editor's groups of formats. */
type FormatGroup =
  | "brazil"
  | "europe"
  | "unitedKingdom"
  | "northAmerica"
  | "oceania"
  | "vehicles"
  | "banking"
  | "products"
  | "internet";

/** Every text format, grouped as the field editor offers them. */
export const FORMAT_GROUPS: readonly { group: FormatGroup; formats: readonly TextFormat[] }[] = [
  {
    group: "brazil",
    formats: [
      "br_cpf",
      "br_cnpj",
      "br_ie",
      "br_cep",
      "br_pix_key",
      "br_pis",
      "br_ncm",
      "br_cest",
      "br_cfop",
      "br_nfe_key",
      "br_boleto",
      "br_renavam",
      "br_bank_branch",
      "br_bank_account",
    ],
  },
  {
    group: "europe",
    formats: [
      "eu_vat",
      "it_codice_fiscale",
      "it_partita_iva",
      "pt_nif",
      "es_nif",
      "fr_siren",
      "fr_siret",
      "fr_tva",
      "de_ust_idnr",
      "de_steuernummer",
      "de_leitweg_id",
    ],
  },
  { group: "unitedKingdom", formats: ["uk_vat", "uk_company_number", "uk_utr", "uk_postcode", "uk_sort_code"] },
  { group: "northAmerica", formats: ["us_ein", "us_tin", "us_zip", "us_routing_number", "ca_bn", "ca_qst"] },
  { group: "oceania", formats: ["au_abn", "au_acn", "nz_ird"] },
  { group: "vehicles", formats: ["license_plate", "vin"] },
  { group: "banking", formats: ["iban", "bic"] },
  { group: "products", formats: ["ean", "isbn", "hs_code", "imei", "microchip"] },
  { group: "internet", formats: ["domain", "ip", "cidr", "mac", "uuid", "semver"] },
];

/** A value as read for its kind. */
export interface Read {
  /** What it compares by. */
  key: string;
  /** How it's shown (when valid; an invalid one shows as written). */
  display: string;
  valid: boolean;
  /** Which kind of value: a Pix key's (`cpf`, `cnpj`, `email`, `phone`, `evp`), a barcode's, an ISBN's, an IP's, a plate's country. */
  kind?: string;
}

/**
 * What a field of `type` (with `format`, text only; or its own `pattern` and `mask`) holds: undefined for values
 * compared as written.
 */
export function kindOf(
  type: FieldType,
  format?: TextFormat | null,
  own?: { pattern?: string | null; mask?: string | null },
): Kind | undefined {
  if (type === "email" || type === "phone" || type === "url") {
    return type;
  }
  if (type !== "text") {
    return undefined;
  }
  return format ?? ((own?.pattern ?? own?.mask ?? null) === null ? undefined : "custom");
}

/** Reads in a region (phones, plates, VINs; Brazil's IE in a state). */
export const regional = (k: Kind | undefined): boolean =>
  k === "phone" || k === "license_plate" || k === "vin" || k === "br_ie";

const ok = (k: string, display: string, of?: string): Read => ({
  key: k,
  display,
  valid: true,
  ...(of && { kind: of }),
});
const bad = (k: string, display: string, of?: string): Read => ({
  key: k,
  display,
  valid: false,
  ...(of && { kind: of }),
});
const withKind = (r: Read | null, of: string): Read | null => r && { ...r, kind: of };

const DIGITS = /^[0-9]+$/u;
const DIGIT = /[0-9]/u;
const ALNUM = /^[0-9A-Z]+$/u;
const BR_SEPARATORS = /[./\- ]/gu;
const CODE_SEPARATORS = /[ .-]/gu;
const IBAN_SHAPE = /^[A-Z]{2}[0-9]{2}[0-9A-Z]+$/u;
const BIC_LETTERS = /^[A-Z]{6}/u;
const ISBN_LEAD = /^[: ]+/u;
const ISBN10 = /^[0-9]{9}[0-9X]$/u;
const HEX = /^[0-9a-f]+$/u;
const OCTET = /^(0|[1-9][0-9]{0,2})$/u;
const IPV6_CHARS = /^[0-9a-fA-F:.]+$/u;
const HEXTET = /^[0-9a-fA-F]{1,4}$/u;
const PREFIX = /^\+?[0-9]+$/u;
const MAC_SEPARATORS = /[:.-]/u;
const LABEL = /^[a-z0-9-]+$/u;
const LABEL_EDGE = /^-|-$/u;
const NOT_HOST = /[\s/:]/u;
const SPACE = /\s/u;
const SPACES = /\s+/u;
const BAD_LOCAL = /[\s<>()[\],;:"@]/u;
const HOST_END = /[/?#]/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const OLD_BR_PLATE = /^[A-Z]{3}[0-9]{4}$/u;
const NOT_PHONE = /[^0-9+]/gu;
const ALNUM_OR_EMPTY = /^[0-9A-Z]*$/u;
const SPACES_ALL = /\s+/gu;
const LEADING_ZEROS = /^0+/u;
const TRAILING_ZEROS = /0+$/u;
const TRAILING_DOT = /\.$/u;
const LIST_SEPARATOR = /[,;]/u;

// ---------------------------------------------------------------------------------------------------------------
// Brazil's.

/** Without the separators these are written with (`.`, `-`, `/`, spaces), uppercased. */
const brCompact = (s: string): string => s.replace(BR_SEPARATORS, "").toUpperCase();

/** The mod-11 check digit of `values` with `weights`: 0 when the remainder is under 2. */
function mod11(values: readonly number[], weights: readonly number[]): number {
  const r = values.reduce((sum, v, i) => sum + v * (weights[i] ?? 0), 0) % 11;
  return r < 2 ? 0 : 11 - r;
}

function cpf(s: string): Read | null {
  const c = brCompact(s);
  if (c.length !== 11 || !DIGITS.test(c)) {
    return null;
  }
  const d = [...c].map(Number);
  const valid =
    mod11(d.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]) === d[9] &&
    mod11(d.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]) === d[10] &&
    d.some((x) => x !== d[0]);
  const display = `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}`;
  return valid ? ok(c, display) : bad(c, display);
}

const CNPJ_W1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_W2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** A CNPJ's check digits for its first 12 characters (uppercase letters or digits, each its ASCII code minus 48). */
export function cnpjCheck(base: string): [number, number] | null {
  if (base.length !== 12 || !ALNUM.test(base)) {
    return null;
  }
  const v = [...base].map((ch) => ch.charCodeAt(0) - 48);
  const d1 = mod11(v, CNPJ_W1);
  return [d1, mod11([...v, d1], CNPJ_W2)];
}

function cnpj(s: string): Read | null {
  const c = brCompact(s);
  if (c.length !== 14 || !ALNUM.test(c.slice(0, 12)) || !DIGITS.test(c.slice(12))) {
    return null;
  }
  const check = cnpjCheck(c.slice(0, 12));
  const valid = check !== null && `${check[0]}${check[1]}` === c.slice(12) && [...c].some((x) => x !== c[0]);
  const display = `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
  return valid ? ok(c, display) : bad(c, display);
}

function cep(s: string): Read | null {
  const c = brCompact(s);
  if (c.length !== 8 || !DIGITS.test(c)) {
    return null;
  }
  const display = `${c.slice(0, 5)}-${c.slice(5)}`;
  return c === "00000000" ? bad(c, display) : ok(c, display);
}

/** A Pix key, told apart as the server does: email, random key (EVP), +55 phone, CPF, CNPJ, then a national phone. */
function pix(s: string): Read | null {
  if (s.includes("@")) {
    return withKind(email(s), "email");
  }
  const evp = uuid(s);
  if (evp) {
    return { ...evp, kind: "evp" };
  }
  if (s.startsWith("+")) {
    return withKind(phone(s, "BR"), "phone");
  }
  const asCpf = cpf(s);
  if (asCpf?.valid) {
    return { ...asCpf, kind: "cpf" };
  }
  const asCnpj = cnpj(s);
  if (asCnpj) {
    return { ...asCnpj, kind: "cnpj" };
  }
  const tel = phone(s, "BR");
  if (tel?.valid) {
    return { ...tel, kind: "phone" };
  }
  return withKind(asCpf, "cpf");
}

// ---------------------------------------------------------------------------------------------------------------
// Codes: IBAN, BIC, GTIN, ISBN, UUID.

/** Without spaces, hyphens and dots, uppercased. */
const codeCompact = (s: string): string => s.replace(CODE_SEPARATORS, "").toUpperCase();

/** IBAN lengths by country (SWIFT's registry), as on the server. */
const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BI: 27,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DJ: 27,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HN: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MN: 20,
  MR: 27,
  MT: 31,
  MU: 30,
  NI: 28,
  NL: 18,
  NO: 15,
  OM: 23,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SO: 23,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
  YE: 30,
};

/** In groups of `n`, space-separated. */
const groups = (s: string, n: number): string => s.match(new RegExp(`.{1,${n}}`, "gu"))?.join(" ") ?? s;

function iban(s: string): Read | null {
  const c = codeCompact(s.startsWith("IBAN") ? s.slice(4) : s);
  if (c.length < 15 || c.length > 34 || !IBAN_SHAPE.test(c)) {
    return null;
  }
  let rem = 0;
  for (const ch of c.slice(4) + c.slice(0, 4)) {
    const v = DIGIT.test(ch) ? Number(ch) : ch.charCodeAt(0) - 55;
    rem = (rem * (v >= 10 ? 100 : 10) + v) % 97;
  }
  const display = groups(c, 4);
  return IBAN_LENGTHS[c.slice(0, 2)] === c.length && rem === 1 ? ok(c, display) : bad(c, display);
}

function bic(s: string): Read | null {
  const c = codeCompact(s);
  if ((c.length !== 8 && c.length !== 11) || !ALNUM.test(c)) {
    return null;
  }
  return BIC_LETTERS.test(c) ? ok(c, c) : bad(c, c);
}

/** The GS1 check digit of `digits` (all but the check digit): weights 3, 1, 3… from the right. */
function gs1(digits: string): number {
  const sum = [...digits].reverse().reduce((acc, d, i) => acc + Number(d) * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10;
}

const GTIN_KINDS: ReadonlyMap<number, string> = new Map([
  [8, "ean8"],
  [12, "upc_a"],
  [13, "ean13"],
  [14, "gtin14"],
]);

function ean(s: string): Read | null {
  const c = codeCompact(s);
  const of = GTIN_KINDS.get(c.length);
  if (!(of && DIGITS.test(c))) {
    return null;
  }
  const k = c.padStart(14, "0");
  return gs1(c.slice(0, -1)) === Number(c.slice(-1)) ? ok(k, c, of) : bad(k, c, of);
}

function isbn(s: string): Read | null {
  const c = codeCompact((s.startsWith("ISBN") ? s.slice(4) : s).replace(ISBN_LEAD, ""));
  if (c.length === 10 && ISBN10.test(c)) {
    const sum = [...c].reduce((acc, ch, i) => acc + (ch === "X" ? 10 : Number(ch)) * (10 - i), 0);
    const base = `978${c.slice(0, 9)}`;
    const k = base + String(gs1(base));
    return sum % 11 === 0 ? ok(k, c, "isbn10") : bad(k, c, "isbn10");
  }
  if (c.length === 13 && DIGITS.test(c)) {
    const valid = (c.startsWith("978") || c.startsWith("979")) && gs1(c.slice(0, 12)) === Number(c[12]);
    return valid ? ok(c, c, "isbn13") : bad(c, c, "isbn13");
  }
  return null;
}

function uuid(s: string): Read | null {
  let t = s.toLowerCase();
  t = t.startsWith("urn:uuid:") ? t.slice(9) : t;
  t = t.startsWith("{") && t.endsWith("}") ? t.slice(1, -1) : t;
  const hex = t.replaceAll("-", "");
  const dashes = !t.includes("-") || (t.length === 36 && [8, 13, 18, 23].every((i) => t[i] === "-"));
  if (hex.length !== 32 || !HEX.test(hex) || !dashes) {
    return null;
  }
  const k = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  return ok(k, k);
}

// ---------------------------------------------------------------------------------------------------------------
// The internet's.

/** An IPv4 address as Rust reads one: four decimal octets, no leading zeros. */
function ipv4(s: string): number[] | null {
  const parts = s.split(".");
  if (parts.length !== 4 || !parts.every((p) => OCTET.test(p) && Number(p) <= 255)) {
    return null;
  }
  return parts.map(Number);
}

/** An IPv6 address's eight groups (an IPv4 tail allowed), as Rust reads one. */
function ipv6(s: string): number[] | null {
  if (!IPV6_CHARS.test(s) || s.split("::").length > 2) {
    return null;
  }
  const side = (t: string): number[] | null => {
    if (t === "") {
      return [];
    }
    const out: number[] = [];
    const parts = t.split(":");
    for (const [i, p] of parts.entries()) {
      if (i === parts.length - 1 && p.includes(".")) {
        const v4 = ipv4(p);
        if (!v4) {
          return null;
        }
        out.push(((v4[0] ?? 0) << 8) | (v4[1] ?? 0), ((v4[2] ?? 0) << 8) | (v4[3] ?? 0));
      } else if (HEXTET.test(p)) {
        out.push(Number.parseInt(p, 16));
      } else {
        return null;
      }
    }
    return out;
  };
  const [head = "", tail] = s.split("::");
  const a = side(head);
  const b = tail === undefined ? [] : side(tail);
  if (!(a && b)) {
    return null;
  }
  if (tail === undefined) {
    return a.length === 8 ? a : null;
  }
  const fill = 8 - a.length - b.length;
  return fill >= 1 ? [...a, ...Array<number>(fill).fill(0), ...b] : null;
}

/** An IPv6 address as Rust shows it (RFC 5952: the longest run of zero groups, of two or more, as `::`). */
function showIpv6(g: readonly number[]): string {
  if (g.slice(0, 5).every((x) => x === 0) && g[5] === 0xff_ff) {
    const [hi = 0, lo = 0] = g.slice(6);
    return `::ffff:${hi >> 8}.${hi & 255}.${lo >> 8}.${lo & 255}`;
  }
  let best = [0, 0];
  let i = 0;
  while (i < 8) {
    if (g[i] === 0) {
      let j = i;
      while (j < 8 && g[j] === 0) {
        j += 1;
      }
      if (j - i > (best[1] ?? 0) - (best[0] ?? 0)) {
        best = [i, j];
      }
      i = j;
    } else {
      i += 1;
    }
  }
  const hex = (xs: readonly number[]) => xs.map((x) => x.toString(16)).join(":");
  const [from = 0, to = 0] = best;
  return to - from > 1 ? `${hex(g.slice(0, from))}::${hex(g.slice(to))}` : hex(g);
}

/** An IP address: canonical text and family. */
function ipOf(s: string): { text: string; v4: boolean } | null {
  const v4 = ipv4(s);
  if (v4) {
    return { text: v4.join("."), v4: true };
  }
  const v6 = ipv6(s);
  return v6 && { text: showIpv6(v6), v4: false };
}

function ip(s: string): Read | null {
  const t = s.startsWith("[") && s.endsWith("]") ? s.slice(1, -1) : s;
  const a = ipOf(t);
  return a && ok(a.text, a.text, a.v4 ? "ipv4" : "ipv6");
}

function cidr(s: string): Read | null {
  const at = s.indexOf("/");
  if (at < 0) {
    return null;
  }
  const a = ipOf(s.slice(0, at).trim());
  const p = s.slice(at + 1).trim();
  if (!(a && PREFIX.test(p)) || Number(p) > 255) {
    return null;
  }
  const k = `${a.text}/${Number(p)}`;
  const of = a.v4 ? "ipv4" : "ipv6";
  return Number(p) <= (a.v4 ? 32 : 128) ? ok(k, k, of) : bad(k, k, of);
}

function mac(s: string): Read | null {
  const parts = s.split(MAC_SEPARATORS);
  const shaped =
    (parts.length === 6 && parts.every((g) => g.length === 2)) ||
    (parts.length === 3 && s.includes(".") && parts.every((g) => g.length === 4)) ||
    (parts.length === 1 && s.length === 12);
  const hex = parts.join("").toLowerCase();
  if (!shaped || hex.length !== 12 || !HEX.test(hex)) {
    return null;
  }
  const k = groups(hex, 2).replaceAll(" ", ":");
  return ok(k, k);
}

/** A host's ASCII form as WHATWG URLs (and the server's `url` crate) make it: lowercase, IDNs in punycode. */
function asciiHost(s: string): string | null {
  try {
    return new URL(`http://${s}`).hostname;
  } catch {
    return null;
  }
}

const IPV4_HOST = /^[0-9]+\.[0-9]+\.[0-9]+\.[0-9]+$/u;

/** A domain name's ASCII form, if it is one: LDH labels up to 63 long, at least two, the last not all digits. */
function asciiDomain(s: string): string | null {
  const t = s.endsWith(".") ? s.slice(0, -1) : s;
  if (t === "" || NOT_HOST.test(t)) {
    return null;
  }
  const d = asciiHost(t);
  if (!d || d.startsWith("[") || IPV4_HOST.test(d)) {
    return null;
  }
  const labels = d.split(".");
  const valid =
    d.length <= 253 &&
    labels.length >= 2 &&
    labels.every((l) => l.length >= 1 && l.length <= 63 && LABEL.test(l) && !LABEL_EDGE.test(l)) &&
    !DIGITS.test(labels.at(-1) ?? "");
  return valid ? d : null;
}

function domain(s: string): Read | null {
  const written = (s.endsWith(".") ? s.slice(0, -1) : s).toLowerCase();
  if (SPACE.test(written) || !written.includes(".")) {
    return null;
  }
  const k = asciiDomain(written);
  return k ? ok(k, written) : bad(written, written);
}

function email(s: string): Read | null {
  const t = s.startsWith("mailto:") ? s.slice(7) : s;
  const at = t.lastIndexOf("@");
  if (at < 0) {
    return null;
  }
  const local = t.slice(0, at);
  const host = t.slice(at + 1).toLowerCase();
  const display = `${local}@${host}`;
  const localOk = local.length >= 1 && local.length <= 64 && !BAD_LOCAL.test(local);
  const d = localOk ? asciiDomain(host) : null;
  return d ? ok(`${local}@${d}`, display) : bad(display, display);
}

function url(s: string): Read | null {
  if (SPACE.test(s)) {
    return null;
  }
  const schemed = s.includes("://") || s.startsWith("mailto:") || s.startsWith("tel:");
  const full = schemed ? s : `https://${s}`;
  let u: URL;
  try {
    u = new URL(full);
  } catch {
    return null;
  }
  // The host as written (an international one isn't shown in punycode), lowercased.
  const rest = full.includes("://") ? full.slice(full.indexOf("://") + 3) : "";
  let host = rest.split(HOST_END)[0] ?? "";
  host = host.slice(host.lastIndexOf("@") + 1);
  host = (host.startsWith("[") ? host : (host.split(":")[0] ?? "")).toLowerCase();
  const web = ["http:", "https:", "ftp:"].includes(u.protocol);
  const h = u.hostname;
  const valid =
    !web || (h !== "" && (h === "localhost" || h.startsWith("[") || IPV4_HOST.test(h) || asciiDomain(h) !== null));
  const domainShown = host === "" ? u.href : host.startsWith("www.") ? host.slice(4) : host;
  return valid ? ok(u.href, domainShown) : bad(u.href, domainShown);
}

// ---------------------------------------------------------------------------------------------------------------
// Phones (libphonenumber-js; the server uses a port of the same library and metadata).

const supported = (r: string | null | undefined): r is CountryCode => Boolean(r && isSupportedCountry(r));

function phone(s: string, region: string | null | undefined): Read | null {
  // An international number is read without a region, as on the server.
  const n = parsePhoneNumberFromString(s, s.startsWith("+") || !supported(region) ? undefined : region);
  if (!n) {
    return null;
  }
  const home = supported(region) && getCountryCallingCode(region) === n.countryCallingCode;
  const display = home ? n.formatNational() : n.formatInternational();
  return n.isValid() ? ok(n.number, display) : bad(n.number, display);
}

/** The regions phones can be read in (ISO 3166-1 alpha-2). */
export const phoneRegions = (): readonly string[] => getCountries();

/** A region's name in `locale` (its code when the browser has none). */
export function regionName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code === "UK" ? "GB" : code) ?? code;
  } catch {
    return code;
  }
}

/** An example phone number of `region` (a mobile's, national form): a placeholder. */
function phoneExample(region: string | null | undefined): string | undefined {
  return supported(region) ? getExampleNumber(region, examples)?.formatNational() : undefined;
}

// ---------------------------------------------------------------------------------------------------------------
// License plates.

/** The region accepting every country's plates. */
export const ANY_REGION = "any";

/** (country, pattern on the compact plate, how it's shown; "" shows it as written, uppercased): as on the server. */
const PLATES: readonly [string, RegExp, string][] = [
  ["BR", /^([A-Z]{3}[0-9][A-Z][0-9]{2})$/u, "$1"],
  ["BR", /^([A-Z]{3})([0-9]{4})$/u, "$1-$2"],
  ["AR", /^([A-Z]{2})([0-9]{3})([A-Z]{2})$/u, "$1 $2 $3"],
  ["AR", /^([A-Z]{3})([0-9]{3})$/u, "$1 $2"],
  ["IT", /^([A-Z]{2})([0-9]{3})([A-Z]{2})$/u, "$1 $2 $3"],
  ["PT", /^([A-Z]{2})([0-9]{2})([A-Z]{2})$/u, "$1-$2-$3"],
  ["PT", /^([0-9]{2})([A-Z]{2})([0-9]{2})$/u, "$1-$2-$3"],
  ["PT", /^([0-9]{2})([0-9]{2})([A-Z]{2})$/u, "$1-$2-$3"],
  ["PT", /^([A-Z]{2})([0-9]{2})([0-9]{2})$/u, "$1-$2-$3"],
  ["GB", /^([A-Z]{2}[0-9]{2})([A-Z]{3})$/u, "$1 $2"],
  ["GB", /^([A-Z][0-9]{1,3})([A-Z]{3})$/u, "$1 $2"],
  ["GB", /^([A-Z]{3})([0-9]{1,3}[A-Z])$/u, "$1 $2"],
  ["GB", /^([A-Z]{1,3})([0-9]{1,4})$/u, "$1 $2"],
  ["GB", /^([0-9]{1,4})([A-Z]{1,3})$/u, "$1 $2"],
  ["IE", /^([0-9]{2,3})([A-Z]{1,2})([0-9]{1,6})$/u, "$1-$2-$3"],
  ["DE", /^[A-ZÄÖÜ]{1,3}[A-Z]{1,2}[1-9][0-9]{0,3}[EH]?$/u, ""],
  ["FR", /^([A-Z]{2})([0-9]{3})([A-Z]{2})$/u, "$1-$2-$3"],
  ["FR", /^([0-9]{1,4})([A-Z]{1,3})([0-9]{2}|2A|2B)$/u, "$1 $2 $3"],
  ["ES", /^([0-9]{4})([BCDFGHJKLMNPRSTVWXYZ]{3})$/u, "$1 $2"],
  ["ES", /^([A-Z]{1,2})([0-9]{4})([A-Z]{1,2})$/u, "$1-$2-$3"],
  ["US", /^[A-Z0-9]{2,8}$/u, ""],
  ["CA", /^[A-Z0-9]{2,8}$/u, ""],
  ["AU", /^[A-Z0-9]{2,8}$/u, ""],
  ["NZ", /^[A-Z0-9]{1,6}$/u, ""],
];

/** The countries with plate patterns, in the order the field editor lists them. */
export const PLATE_REGIONS: readonly string[] = [...new Set(PLATES.map(([c]) => c))];

const canonicalRegion = (r: string): string => (r === "UK" ? "GB" : r);

function plate(s: string, region: string | null | undefined): Read | null {
  const c = [...s]
    .filter((ch) => LETTER_OR_DIGIT.test(ch))
    .join("")
    .toUpperCase();
  if ([...c].length < 1 || [...c].length > 10) {
    return null;
  }
  const r = region ? canonicalRegion(region) : null;
  const known = r && PLATE_REGIONS.includes(r) ? r : null;
  const found = PLATES.find(([of, pattern]) => (known === null || known === of) && pattern.test(c));
  // Brazil's old plates key as their Mercosul form: the fifth character, 0–9, becomes A–J.
  const k =
    known === "BR" && OLD_BR_PLATE.test(c) ? c.slice(0, 4) + String.fromCharCode(65 + Number(c[4])) + c.slice(5) : c;
  if (!found) {
    return bad(k, c);
  }
  const [country, re, template] = found;
  const display =
    known === null
      ? c
      : template === ""
        ? s.split(SPACES).filter(Boolean).join(" ").toUpperCase()
        : c.replace(re, template);
  return ok(k, display, country);
}

// ---------------------------------------------------------------------------------------------------------------
// Check digits (server/src/formats/digits.rs).

/** Without spaces, dots, hyphens and slashes, uppercased. */
const idCompact = (s: string): string => s.replace(BR_SEPARATORS, "").toUpperCase();
const allDigits = (s: string): boolean => DIGITS.test(s);
const digitsOf = (s: string): number[] => [...s].map(Number);
const weighted = (v: readonly number[], w: readonly number[]): number =>
  v.reduce((sum, x, i) => sum + x * (w[i] ?? 0), 0);

/** Luhn's check over all of `s` (digits, the last the check digit). */
function luhn(s: string): boolean {
  if (!allDigits(s)) {
    return false;
  }
  const sum = digitsOf(s)
    .reverse()
    .reduce((acc, d, i) => {
      const x = i % 2 === 0 ? d : d * 2;
      return acc + (x > 9 ? x - 9 : x);
    }, 0);
  return sum % 10 === 0;
}

/** Σ of `v` from the right times 2…9, 2… (NF-e keys', boletos'). */
const cycling = (v: readonly number[]): number => [...v].reverse().reduce((acc, d, i) => acc + d * (2 + (i % 8)), 0);
/** 11 minus the remainder, 0 when it's under 2. */
const eleven = (sum: number): number => (sum % 11 < 2 ? 0 : 11 - (sum % 11));
/** Boletos' mod 10: weights 2, 1… from the right, products' digits summed. */
function mod10(v: readonly number[]): number {
  const sum = [...v].reverse().reduce((acc, d, i) => {
    const x = d * (i % 2 === 0 ? 2 : 1);
    return acc + Math.floor(x / 10) + (x % 10);
  }, 0);
  return (10 - (sum % 10)) % 10;
}
/** ISO 7064 MOD 11,10's check digit. */
function iso7064(v: readonly number[]): number {
  let product = 10;
  for (const d of v) {
    let sum = (d + product) % 10;
    if (sum === 0) {
      sum = 10;
    }
    product = (2 * sum) % 11;
  }
  return (11 - product) % 10;
}
/** Letters and digits (letters 10–35) mod 97: ISO 7064 MOD 97-10. */
function mod97(s: string): number {
  let r = 0;
  for (const ch of s.toUpperCase()) {
    const v = DIGIT.test(ch) ? Number(ch) : ch.charCodeAt(0) - 55;
    r = (r * (v >= 10 ? 100 : 10) + v) % 97;
  }
  return r;
}
/** In groups of `widths`, joined by `sep` (what's left in a last group). */
function grouped(c: string, widths: readonly number[], sep: string): string {
  const out: string[] = [];
  let at = 0;
  for (const w of widths) {
    if (at >= c.length) {
      break;
    }
    out.push(c.slice(at, at + w));
    at += w;
  }
  if (at < c.length) {
    out.push(c.slice(at));
  }
  return out.join(sep);
}
/** `c` into `mask`'s `0`s. */
function masked(c: string, m: string): string {
  let i = 0;
  return [...m]
    .map((x) => {
      if (x !== "0") {
        return x;
      }
      const d = c[i] ?? "0";
      i += 1;
      return d;
    })
    .join("");
}
const checked = (valid: boolean, k: string, display: string, of?: string): Read =>
  valid ? ok(k, display, of) : bad(k, display, of);

// ---------------------------------------------------------------------------------------------------------------
// Brazil's, more (br.rs).

const brDigits = (s: string, ...lengths: number[]): string | null => {
  const c = idCompact(s);
  return allDigits(c) && lengths.includes(c.length) ? c : null;
};

function ncm(s: string): Read | null {
  const c = brDigits(s, 8);
  return c ? ok(c, masked(c, "0000.00.00")) : null;
}

function cest(s: string): Read | null {
  const c = brDigits(s, 7);
  return c ? ok(c, masked(c, "00.000.00")) : null;
}

const CFOP_FIRST = /^[1-35-7]/u;

function cfop(s: string): Read | null {
  const c = brDigits(s, 4);
  return c ? checked(CFOP_FIRST.test(c), c, masked(c, "0.000")) : null;
}

function nfeKey(s: string): Read | null {
  const t = s.startsWith("NFe") || s.startsWith("CTe") ? s.slice(3) : s;
  const c = brDigits(t, 44);
  if (!c) {
    return null;
  }
  const v = digitsOf(c);
  return checked(eleven(cycling(v.slice(0, 43))) === v[43], c, grouped(c, Array<number>(11).fill(4), " "));
}

function boleto(s: string): Read | null {
  const c = idCompact(s);
  if (!allDigits(c)) {
    return null;
  }
  const v = digitsOf(c);
  if (c.length === 47) {
    const fields =
      mod10(v.slice(0, 9)) === v[9] && mod10(v.slice(10, 20)) === v[20] && mod10(v.slice(21, 31)) === v[31];
    const bar = [...v.slice(0, 4), ...v.slice(33, 47), ...v.slice(4, 9), ...v.slice(10, 20), ...v.slice(21, 31)];
    const d = 11 - (cycling(bar) % 11);
    const general = d === 0 || d === 10 || d === 11 ? 1 : d;
    const display = `${c.slice(0, 5)}.${c.slice(5, 10)} ${c.slice(10, 15)}.${c.slice(15, 21)} ${c.slice(21, 26)}.${c.slice(26, 32)} ${c.slice(32, 33)} ${c.slice(33)}`;
    return checked(fields && general === v[32], c, display);
  }
  if (c.length === 48 && v[0] === 8) {
    const ten = v[2] === 6 || v[2] === 7;
    const dv = (x: readonly number[]) => (ten ? mod10(x) : eleven(cycling(x)));
    const blocks = [0, 1, 2, 3].every((i) => dv(v.slice(i * 12, i * 12 + 11)) === v[i * 12 + 11]);
    const bar = [0, 1, 2, 3].flatMap((i) => v.slice(i * 12, i * 12 + 11));
    const valid = blocks && (v[2] ?? 0) >= 6 && dv([...bar.slice(0, 3), ...bar.slice(4)]) === bar[3];
    const display = [0, 1, 2, 3].map((i) => `${c.slice(i * 12, i * 12 + 11)}-${c.slice(i * 12 + 11, i * 12 + 12)}`);
    return checked(valid, c, display.join(" "));
  }
  return c.length >= 44 && c.length <= 48 ? bad(c, c) : null;
}

function pis(s: string): Read | null {
  const c = brDigits(s, 11);
  if (!c) {
    return null;
  }
  const v = digitsOf(c);
  const d = 11 - (weighted(v.slice(0, 10), [3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) % 11);
  return checked((d >= 10 ? 0 : d) === v[10] && c !== "00000000000", c, masked(c, "000.00000.00-0"));
}

function renavam(s: string): Read | null {
  const raw = brDigits(s, 9, 11);
  if (!raw) {
    return null;
  }
  const c = raw.padStart(11, "0");
  const v = digitsOf(c);
  const d = ((weighted(v.slice(0, 10), [3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) * 10) % 11) % 10;
  return checked(d === v[10] && c !== "00000000000", c, c);
}

const BRANCH = /^[0-9]{4}[0-9X]?$/u;
const ACCOUNT = /^[0-9]{1,13}[0-9X]$/u;

function bankBranch(s: string): Read | null {
  const c = idCompact(s);
  return BRANCH.test(c) ? ok(c, c.length === 5 ? `${c.slice(0, 4)}-${c.slice(4)}` : c) : null;
}

function bankAccount(s: string): Read | null {
  const c = idCompact(s);
  return ACCOUNT.test(c) ? ok(c, `${c.slice(0, -1)}-${c.slice(-1)}`) : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Brazil's IE, each state's rules (br_ie.rs, after Sintegra's manual).

/** Brazil's states (ISO 3166-2 after `BR-`), and their names. */
export const BR_STATES: Readonly<Record<string, string>> = {
  AC: "Acre",
  AL: "Alagoas",
  AM: "Amazonas",
  AP: "Amapá",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MG: "Minas Gerais",
  MS: "Mato Grosso do Sul",
  MT: "Mato Grosso",
  PA: "Pará",
  PB: "Paraíba",
  PE: "Pernambuco",
  PI: "Piauí",
  PR: "Paraná",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RO: "Rondônia",
  RR: "Roraima",
  RS: "Rio Grande do Sul",
  SC: "Santa Catarina",
  SE: "Sergipe",
  SP: "São Paulo",
  TO: "Tocantins",
};
const STATES = Object.keys(BR_STATES);

/** A field region's state (`BR-SP` → `SP`), if it names one. */
const stateOf = (region: string | null | undefined): string | undefined => {
  const s = region?.startsWith("BR-") ? region.slice(3) : undefined;
  return s && s in BR_STATES ? s : undefined;
};

/** Σ of `base`'s digits from the right times `weights` (cycled), mod `div`. */
const ieMod = (base: string, weights: readonly number[], div: number): number =>
  [...base].reverse().reduce((acc, d, i) => acc + Number(d) * (weights[i % weights.length] ?? 0), 0) % div;
const W29 = [2, 3, 4, 5, 6, 7, 8, 9];
const range = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, i) => from + i);
const elevenR = (r: number): number => (r < 2 ? 0 : 11 - r);
const tenOf = (d: number): number => (d >= 10 ? 0 : d);
const trivial = (v: string): boolean =>
  v.length === 9 && v === v.slice(0, 8) + String(elevenR(ieMod(v.slice(0, 8), W29, 11)) % 10);
const between = (base: string, lo: number, hi: number): boolean => Number(base) >= lo && Number(base) <= hi;

function ieSp(v: string): boolean {
  const w = [10, 8, 7, 6, 5, 4, 3, 1];
  if (v.startsWith("P")) {
    const rest = v.slice(1);
    return (
      v.length === 13 &&
      allDigits(rest) &&
      v === `P${rest.slice(0, 8)}${ieMod(rest.slice(0, 8), w, 11) % 10}${rest.slice(9, 12)}`
    );
  }
  if (v.length !== 12 || !allDigits(v)) {
    return false;
  }
  const first = v.slice(0, 8);
  const d1 = ieMod(first, w, 11) % 10;
  const d2 = ieMod(`${first}${d1}${v.slice(9, 11)}`, range(2, 10), 11) % 10;
  return v === `${first}${d1}${v.slice(9, 11)}${d2}`;
}

function ieMg(v: string): boolean {
  const base = v.slice(0, 11);
  const withZero = `${v.slice(0, 3)}0${v.slice(3, 11)}`;
  const sum = [...withZero].reverse().reduce((acc, d, i) => {
    const x = Number(d) * (i % 2 === 0 ? 2 : 1);
    return acc + Math.floor(x / 10) + (x % 10);
  }, 0);
  const d1 = ((Math.floor(sum / 10) + 1) * 10 - sum) % 10;
  return v === `${base}${d1}${elevenR(ieMod(`${base}${d1}`, range(2, 11), 11)) % 10}`;
}

/** Two check digits, each 11 minus a remainder (`zero`: 0 when it's 10 or 11, else when under 2). */
function twoDigits(v: string, base: string, weights: readonly number[], tenToZero: boolean): boolean {
  const dv = (b: string) => (tenToZero ? tenOf(11 - ieMod(b, weights, 11)) : elevenR(ieMod(b, weights, 11)) % 10);
  const d1 = dv(base);
  return v === `${base}${d1}${dv(`${base}${d1}`)}`;
}

/** Whether `v` (compact: digits, or SP's rural `P…`) is a valid IE of `state`, rule by rule as on the server. */
function ieIn(state: string, v: string): boolean {
  const n = v.length;
  if (state !== "SP" && !allDigits(v)) {
    return false;
  }
  switch (state) {
    case "AC":
      return n === 13 && v.startsWith("01") && twoDigits(v, v.slice(0, 11), W29, false);
    case "AL": {
      const r = (ieMod(v.slice(0, 8), W29, 11) * 10) % 11;
      return n === 9 && v.startsWith("24") && v === v.slice(0, 8) + String(r === 10 ? 0 : r);
    }
    case "AM":
    case "PB":
    case "SE":
    case "ES":
    case "PI":
    case "SC":
      return trivial(v);
    case "AP": {
      if (n !== 9 || !v.startsWith("03")) {
        return false;
      }
      const base = v.slice(0, 8);
      const [p, d] = between(base, 3_000_001, 3_017_000)
        ? [5, 0]
        : between(base, 3_017_001, 3_019_022)
          ? [9, 1]
          : [0, 0];
      const r = (ieMod(base, [2, 3, 4, 5, 6, 7, 8, 9, 1], 11) + p) % 11;
      return v === base + String(r === 1 ? 0 : r === 0 ? d : 11 - r);
    }
    case "BA": {
      if (n !== 8 && n !== 9) {
        return false;
      }
      const base = v.slice(0, n - 2);
      const w2 = n === 9 ? [...range(2, 7), 8] : range(2, 7);
      const w1 = n === 9 ? [...range(2, 8), 9] : range(2, 8);
      const lead = v[n === 9 ? 1 : 0] ?? "";
      let d1: number;
      let d2: number;
      if ("0123458".includes(lead)) {
        const r2 = ieMod(base, w2, 10);
        d2 = r2 === 0 ? 0 : 10 - r2;
        const r1 = ieMod(`${base}${d2}`, w1, 10);
        d1 = r1 === 0 ? 0 : 10 - r1;
      } else {
        d2 = elevenR(ieMod(base, w2, 11));
        d1 = elevenR(ieMod(`${base}${d2}`, w1, 11));
      }
      return v === `${base}${d1 % 10}${d2 % 10}`;
    }
    case "CE":
      return n === 9 && v.startsWith("06") && trivial(v);
    case "DF":
      return n === 13 && twoDigits(v, v.slice(0, 11), W29, false);
    case "GO": {
      if (n !== 9 || !["10", "11", "15"].includes(v.slice(0, 2))) {
        return false;
      }
      const base = v.slice(0, 8);
      if (base === "11094402") {
        return v[8] === "0" || v[8] === "1";
      }
      const r = ieMod(base, W29, 11);
      const dv = r === 1 && between(base, 10_103_105, 10_119_997) ? 1 : r < 2 ? 0 : 11 - r;
      return v === base + String(dv);
    }
    case "MA":
      return n === 9 && v.startsWith("12") && trivial(v);
    case "MG":
      return n === 13 && ieMg(v);
    case "MS":
      return v.startsWith("28") && trivial(v);
    case "MT": {
      if (n !== 11 && n !== 9) {
        return false;
      }
      const base = v.slice(0, n - 1);
      return v === base + String(elevenR(ieMod(base, W29, 11)) % 10);
    }
    case "PA":
      return n === 9 && v.startsWith("15") && trivial(v);
    case "PE":
      return n > 2 && twoDigits(v, v.slice(0, n - 2), W29, true);
    case "PR":
      return n === 10 && twoDigits(v, v.slice(0, 8), range(2, 7), true);
    case "RJ":
      return n === 8 && v === v.slice(0, 7) + String(elevenR(ieMod(v.slice(0, 7), range(2, 7), 11)) % 10);
    case "RN": {
      if ((n !== 9 && n !== 10) || !v.startsWith("20")) {
        return false;
      }
      const base = v.slice(0, n - 1);
      const r = (ieMod(base, n === 10 ? [...W29, 10] : W29, 11) * 10) % 11;
      return v === base + String(r === 10 ? 0 : r);
    }
    case "RO": {
      if (n === 9) {
        const base = v.slice(3, 8);
        return v === `${v.slice(0, 3)}${base}${elevenR(ieMod(base, W29, 11)) % 10}`;
      }
      if (n === 14) {
        const r = ieMod(v.slice(0, 13), W29, 11);
        return v === v.slice(0, 13) + String((r <= 1 ? 1 : 11 - r) % 10);
      }
      return false;
    }
    case "RR":
      return (
        n === 9 && v.startsWith("24") && v === v.slice(0, 8) + String(ieMod(v.slice(0, 8), [8, 7, 6, 5, 4, 3, 2, 1], 9))
      );
    case "RS":
      return n === 10 && v === v.slice(0, 9) + String(elevenR(ieMod(v.slice(0, 9), W29, 11)) % 10);
    case "SP":
      return ieSp(v);
    case "TO": {
      if (n !== 9 && n !== 11) {
        return false;
      }
      if (n === 11 && !["01", "02", "03", "99"].includes(v.slice(2, 4))) {
        return false;
      }
      const base = n === 11 ? v.slice(0, 2) + v.slice(4, 10) : v.slice(0, 8);
      return v === v.slice(0, n - 1) + String(elevenR(ieMod(base, W29, 11)) % 10);
    }
    default:
      return false;
  }
}

/** How each state writes its IEs, by length. */
const IE_MASKS: readonly [string, number, string][] = [
  ["AC", 13, "00.000.000/000-00"],
  ["AM", 9, "00.000.000-0"],
  ["BA", 8, "000000-00"],
  ["BA", 9, "0000000-00"],
  ["CE", 9, "00000000-0"],
  ["DF", 13, "00000000000-00"],
  ["ES", 9, "000.000.00-0"],
  ["GO", 9, "00.000.000-0"],
  ["MG", 13, "000.000.000/0000"],
  ["MT", 11, "0000000000-0"],
  ["PA", 9, "00-000000-0"],
  ["PB", 9, "00000000-0"],
  ["PE", 9, "0000000-00"],
  ["PE", 14, "00.0.000.0000000-0"],
  ["PR", 10, "00000000-00"],
  ["RJ", 8, "00.000.00-0"],
  ["RN", 9, "00.000.000-0"],
  ["RN", 10, "00.0.000.000-0"],
  ["RO", 14, "0000000000000-0"],
  ["RR", 9, "00000000-0"],
  ["RS", 10, "000/0000000"],
  ["SC", 9, "000.000.000"],
  ["SE", 9, "00000000-0"],
  ["SP", 12, "000.000.000.000"],
  ["SP", 13, "P-00000000.0/000"],
  ["TO", 11, "00000000000"],
  ["TO", 9, "000000000"],
];
const RURAL = /^P[0-9]+$/u;

function ie(s: string, region: string | null | undefined): Read | null {
  const c = idCompact(s);
  if (c === "ISENTO") {
    return ok(c, c, "exempt");
  }
  if (!(allDigits(c) || RURAL.test(c)) || c.length < 8 || c.length > 14) {
    return null;
  }
  const only = stateOf(region);
  const found = only ? (ieIn(only, c) ? only : undefined) : STATES.find((st) => ieIn(st, c));
  if (!found) {
    return bad(c, c);
  }
  const m = IE_MASKS.find(([st, len]) => st === found && len === c.length)?.[2];
  return ok(c, m ? masked(c.replace("P", ""), m) : c, found);
}

// ---------------------------------------------------------------------------------------------------------------
// Europe's (europe.rs).

const unprefixed = (c: string, prefix: string): string => (c.startsWith(prefix) ? c.slice(prefix.length) : c);

function partitaIva(s: string): Read | null {
  const d = unprefixed(idCompact(s), "IT");
  return d.length === 11 && allDigits(d) ? checked(luhn(d) && d !== "00000000000", d, d) : null;
}

const CF = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/u;
const CF_ODD = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
const ALNUM_16 = /^[0-9A-Z]{16}$/u;
const OMOCODIA = "LMNPQRSTUV";

function codiceFiscale(s: string): Read | null {
  const c = idCompact(s);
  if (c.length === 11 && allDigits(c)) {
    return withKind(partitaIva(c), "company");
  }
  if (!ALNUM_16.test(c)) {
    return null;
  }
  const index = (ch: string) => (DIGIT.test(ch) ? Number(ch) : ch.charCodeAt(0) - 65);
  const sum = [...c.slice(0, 15)].reduce((acc, ch, i) => acc + (i % 2 === 0 ? (CF_ODD[index(ch)] ?? 0) : index(ch)), 0);
  const digit = (ch: string) => (DIGIT.test(ch) ? Number(ch) : Math.max(0, OMOCODIA.indexOf(ch)));
  const day = digit(c[9] ?? "0") * 10 + digit(c[10] ?? "0");
  const valid =
    CF.test(c) && c.charCodeAt(15) - 65 === sum % 26 && ((day >= 1 && day <= 31) || (day >= 41 && day <= 71));
  return checked(valid, c, c, "person");
}

function ptNifOk(d: string): boolean {
  const v = digitsOf(d);
  const first =
    [1, 2, 3, 5, 6, 8, 9].includes(v[0] ?? 0) ||
    ["45", "70", "71", "72", "74", "75", "77", "78", "79"].includes(d.slice(0, 2));
  const r = weighted(v.slice(0, 8), [9, 8, 7, 6, 5, 4, 3, 2]) % 11;
  return first && v[8] === (r < 2 ? 0 : 11 - r);
}

function ptNif(s: string): Read | null {
  const d = unprefixed(idCompact(s), "PT");
  return d.length === 9 && allDigits(d) ? checked(ptNifOk(d), d, d) : null;
}

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const ALNUM_9 = /^[0-9A-Z]{9}$/u;

function cifOk(c: string): boolean {
  const lead = c[0] ?? "";
  if (!"ABCDEFGHJNPQRSUVW".includes(lead)) {
    return false;
  }
  const sum = [...c.slice(1, 8)].reduce((acc, ch, i) => {
    const d = Number(ch);
    return acc + (i % 2 === 0 ? Math.floor((d * 2) / 10) + ((d * 2) % 10) : d);
  }, 0);
  const k = (10 - (sum % 10)) % 10;
  const asDigit = c[8] === String(k);
  const asLetter = c[8] === "JABCDEFGHI"[k];
  return "PQRSNW".includes(lead) ? asLetter : "ABEH".includes(lead) ? asDigit : asDigit || asLetter;
}

function esNif(s: string): Read | null {
  const c = unprefixed(idCompact(s), "ES");
  if (!ALNUM_9.test(c)) {
    return null;
  }
  const mid = c.slice(1, 8);
  const letter = (n: number) => DNI_LETTERS[n % 23];
  const lead = c[0] ?? "";
  if (DIGIT.test(lead) && allDigits(c.slice(0, 8))) {
    return checked(letter(Number(c.slice(0, 8))) === c[8], c, c, "dni");
  }
  if ("XYZ".includes(lead) && allDigits(mid)) {
    return checked(letter("XYZ".indexOf(lead) * 10_000_000 + Number(mid)) === c[8], c, c, "nie");
  }
  if ("KLM".includes(lead) && allDigits(mid)) {
    return checked(letter(Number(mid)) === c[8], c, c, "dni");
  }
  if (lead >= "A" && lead <= "W" && allDigits(mid)) {
    return checked(cifOk(c), c, c, "cif");
  }
  return bad(c, c, "dni");
}

const LA_POSTE = "356000000";

function siren(s: string): Read | null {
  const c = idCompact(s);
  return c.length === 9 && allDigits(c) ? checked(luhn(c) && c !== "000000000", c, grouped(c, [3, 3, 3], " ")) : null;
}

function siret(s: string): Read | null {
  const c = idCompact(s);
  if (c.length !== 14 || !allDigits(c)) {
    return null;
  }
  const valid =
    c.slice(0, 9) === LA_POSTE ? digitsOf(c).reduce((a, b) => a + b, 0) % 5 === 0 : luhn(c) && luhn(c.slice(0, 9));
  return checked(valid, c, grouped(c, [3, 3, 3, 5], " "));
}

const TVA_KEY = /^[0-9A-Z]{2}$/u;

function frTva(s: string): Read | null {
  const rest = unprefixed(idCompact(s), "FR");
  if (rest.length !== 11 || !TVA_KEY.test(rest.slice(0, 2)) || !allDigits(rest.slice(2))) {
    return null;
  }
  const sirenPart = rest.slice(2);
  // A number's key is (12 + 3 × (SIREN mod 97)) mod 97 (exact: SIREN mod 97 needs no big integers).
  const valid = allDigits(rest.slice(0, 2))
    ? Number(rest.slice(0, 2)) === (12 + 3 * (Number(sirenPart) % 97)) % 97
    : luhn(sirenPart);
  return checked(valid, `FR${rest}`, `FR ${rest.slice(0, 2)} ${grouped(sirenPart, [3, 3, 3], " ")}`);
}

function steuernummer(s: string): Read | null {
  const c = idCompact(s);
  if (!allDigits(c) || c.length < 8 || c.length > 14) {
    return null;
  }
  return checked([10, 11, 13].includes(c.length), c, s.trim().split(SPACES).join(" "));
}

function ustIdnr(s: string): Read | null {
  const d = unprefixed(idCompact(s), "DE");
  if (d.length !== 9 || !allDigits(d)) {
    return null;
  }
  const v = digitsOf(d);
  return checked(v[0] !== 0 && iso7064(v.slice(0, 8)) === v[8], `DE${d}`, `DE${d}`);
}

const LEITWEG = /^[0-9]{2,12}(-[0-9A-Z]{1,30})?-[0-9]{2}$/u;
const LEITWEG_CHARS = /^[0-9A-Z-]+$/u;

function leitwegId(s: string): Read | null {
  const c = s.trim().replace(SPACES_ALL, "").toUpperCase();
  if (!(c.includes("-") && LEITWEG_CHARS.test(c))) {
    return null;
  }
  return checked(LEITWEG.test(c) && mod97(c.replaceAll("-", "")) === 1, c, c);
}

const ALNUM_ALL = /^[0-9A-Z]+$/u;
const TWO_LETTERS = /^[A-Z]{2}/u;

function vatEl(n: string): boolean {
  const v = digitsOf(n);
  return (v.slice(0, 8).reduce((acc, d, i) => acc + d * 2 ** (8 - i), 0) % 11) % 10 === v[8];
}

/** Each member's VAT number pattern (after its prefix), and its check: as on the server. */
const VAT: readonly [string, RegExp, ((n: string) => boolean) | null][] = [
  [
    "AT",
    /^U[0-9]{8}$/u,
    (n) => {
      const v = digitsOf(n.slice(1));
      const sum = v.slice(0, 7).reduce((acc, d, i) => {
        const x = d * (i % 2 === 0 ? 1 : 2);
        return acc + Math.floor(x / 10) + (x % 10);
      }, 0);
      return (10 - ((sum + 4) % 10)) % 10 === v[7];
    },
  ],
  ["BE", /^[01][0-9]{9}$/u, (n) => 97 - (Number(n.slice(0, 8)) % 97) === Number(n.slice(8))],
  ["BG", /^[0-9]{9,10}$/u, null],
  ["CY", /^[0-9]{8}[A-Z]$/u, null],
  ["CZ", /^[0-9]{8,10}$/u, null],
  [
    "DE",
    /^[1-9][0-9]{8}$/u,
    (n) => {
      const v = digitsOf(n);
      return iso7064(v.slice(0, 8)) === v[8];
    },
  ],
  ["DK", /^[0-9]{8}$/u, (n) => weighted(digitsOf(n), [2, 7, 6, 5, 4, 3, 2, 1]) % 11 === 0],
  ["EE", /^10[0-9]{7}$/u, null],
  ["EL", /^[0-9]{9}$/u, vatEl],
  ["ES", /^[0-9A-Z][0-9]{7}[0-9A-Z]$/u, (n) => esNif(n)?.valid === true],
  [
    "FI",
    /^[0-9]{8}$/u,
    (n) => {
      const v = digitsOf(n);
      const r = weighted(v.slice(0, 7), [7, 9, 10, 5, 8, 4, 2]) % 11;
      return r === 0 ? v[7] === 0 : r !== 1 && v[7] === 11 - r;
    },
  ],
  ["FR", /^[0-9A-Z]{2}[0-9]{9}$/u, (n) => frTva(n)?.valid === true],
  [
    "HR",
    /^[0-9]{11}$/u,
    (n) => {
      const v = digitsOf(n);
      return iso7064(v.slice(0, 10)) === v[10];
    },
  ],
  ["HU", /^[0-9]{8}$/u, null],
  ["IE", /^([0-9]{7}[A-W][A-IW]?|[0-9][A-Z+*][0-9]{5}[A-W])$/u, null],
  ["IT", /^[0-9]{11}$/u, luhn],
  ["LT", /^([0-9]{9}|[0-9]{12})$/u, null],
  ["LU", /^[0-9]{8}$/u, (n) => Number(n.slice(0, 6)) % 89 === Number(n.slice(6))],
  ["LV", /^[0-9]{11}$/u, null],
  ["MT", /^[1-9][0-9]{7}$/u, null],
  [
    "NL",
    /^[0-9]{9}B[0-9]{2}$/u,
    (n) => {
      const v = digitsOf(n.slice(0, 9));
      const r = weighted(v.slice(0, 8), [9, 8, 7, 6, 5, 4, 3, 2]) % 11;
      return (r !== 10 && r === v[8]) || mod97(`NL${n}`) === 1;
    },
  ],
  [
    "PL",
    /^[0-9]{10}$/u,
    (n) => {
      const v = digitsOf(n);
      const r = weighted(v.slice(0, 9), [6, 5, 7, 2, 3, 4, 5, 6, 7]) % 11;
      return r !== 10 && r === v[9];
    },
  ],
  ["PT", /^[0-9]{9}$/u, ptNifOk],
  ["RO", /^[1-9][0-9]{1,9}$/u, null],
  ["SE", /^[0-9]{10}01$/u, (n) => luhn(n.slice(0, 10))],
  ["SI", /^[1-9][0-9]{7}$/u, null],
  ["SK", /^[1-9][0-9]{9}$/u, (n) => BigInt(n) % 11n === 0n],
  ["XI", /^([0-9]{9}|[0-9]{12}|GD[0-9]{3}|HA[0-9]{3})$/u, (n) => ukVatOk(n)],
];

function euVat(s: string): Read | null {
  const c = idCompact(s);
  if (c.length < 4 || !TWO_LETTERS.test(c) || !ALNUM_ALL.test(c)) {
    return null;
  }
  const prefix = c.startsWith("GR") ? "EL" : c.slice(0, 2);
  const n = c.slice(2);
  const k = `${prefix}${n}`;
  const country = VAT.find(([p]) => p === prefix);
  if (!country) {
    return bad(k, k);
  }
  const [code, pattern, check] = country;
  return checked(pattern.test(n) && (check === null || check(n)), k, k, code);
}

// ---------------------------------------------------------------------------------------------------------------
// The UK's, the US's, Canada's, Australia's and New Zealand's (anglo.rs).

const THREE_DIGITS = /^[0-9]{3}$/u;

function ukVatOk(n: string): boolean {
  if (n.startsWith("GD")) {
    return THREE_DIGITS.test(n.slice(2)) && n.slice(2) < "500";
  }
  if (n.startsWith("HA")) {
    return THREE_DIGITS.test(n.slice(2)) && n.slice(2) >= "500";
  }
  if ((n.length !== 9 && n.length !== 12) || !allDigits(n)) {
    return false;
  }
  const v = digitsOf(n.slice(0, 9));
  const total = weighted(v.slice(0, 7), [8, 7, 6, 5, 4, 3, 2]) + (v[7] ?? 0) * 10 + (v[8] ?? 0);
  return total % 97 === 0 || (total + 55) % 97 === 0;
}

function ukVat(s: string): Read | null {
  const c = idCompact(s);
  const own = c.startsWith("GB") || c.startsWith("XI");
  const prefix = own ? c.slice(0, 2) : "GB";
  const n = own ? c.slice(2) : c;
  const shaped =
    (allDigits(n) && n.length >= 7 && n.length <= 12) || ((n.startsWith("GD") || n.startsWith("HA")) && n.length === 5);
  if (!shaped) {
    return null;
  }
  const display = allDigits(n) ? `${prefix} ${grouped(n, [3, 4, 2, 3], " ")}` : `${prefix} ${n}`;
  return checked(ukVatOk(n), `${prefix}${n}`, display);
}

const COMPANY_PREFIXES = new Set(
  "AC BR CE CS FC FE GE GN GS IC IP LP NA NF NI NL NO NP NR NZ OC OE R0 RC RS SA SC SE SF SG SI SL SO SP SR SZ ZC".split(
    " ",
  ),
);
const ALNUM_8 = /^[0-9A-Z]{8}$/u;

function ukCompanyNumber(s: string): Read | null {
  const c = idCompact(s);
  if (allDigits(c) && c.length <= 8) {
    const k = c.padStart(8, "0");
    return checked(k !== "00000000", k, k);
  }
  if (!(ALNUM_8.test(c) && allDigits(c.slice(2)))) {
    return null;
  }
  return checked(COMPANY_PREFIXES.has(c.slice(0, 2)), c, c);
}

function ukUtr(s: string): Read | null {
  const c = idCompact(s);
  const d = c.endsWith("K") ? c.slice(0, -1) : c;
  if (d.length !== 10 || !allDigits(d)) {
    return null;
  }
  const r = weighted(digitsOf(d.slice(1)), [6, 7, 8, 9, 10, 5, 4, 3, 2]) % 11;
  return checked("21987654321"[r] === d[0], d, grouped(d, [5, 5], " "));
}

const UK_POSTCODE =
  /^(GIR0AA|[A-PR-UWYZ]([0-9][0-9A-HJKPSTUW]?|[A-HK-Y][0-9][0-9ABEHMNPRV-Y]?)[0-9][ABD-HJLNP-UW-Z]{2})$/u;

function ukPostcode(s: string): Read | null {
  const c = idCompact(s);
  if (c.length < 5 || c.length > 7 || !ALNUM_ALL.test(c)) {
    return null;
  }
  return checked(UK_POSTCODE.test(c), c, `${c.slice(0, -3)} ${c.slice(-3)}`);
}

function ukSortCode(s: string): Read | null {
  const c = idCompact(s);
  return c.length === 6 && allDigits(c) ? ok(c, grouped(c, [2, 2, 2], "-")) : null;
}

const NO_EIN = new Set([
  "00",
  "07",
  "08",
  "09",
  "17",
  "18",
  "19",
  "28",
  "29",
  "49",
  "69",
  "70",
  "78",
  "79",
  "89",
  "96",
  "97",
]);
const einPrefix = (d: string): boolean => !NO_EIN.has(d.slice(0, 2));

function ssnOk(d: string): boolean {
  const [area, group, serial] = [d.slice(0, 3), d.slice(3, 5), d.slice(5)];
  const g = Number(group);
  const itin =
    area.startsWith("9") &&
    ((g >= 50 && g <= 65) || (g >= 70 && g <= 88) || (g >= 90 && g <= 92) || (g >= 94 && g <= 99));
  return (itin || (area !== "000" && area !== "666" && !area.startsWith("9"))) && group !== "00" && serial !== "0000";
}

function usEin(s: string): Read | null {
  const c = idCompact(s);
  return c.length === 9 && allDigits(c) ? checked(einPrefix(c), c, `${c.slice(0, 2)}-${c.slice(2)}`) : null;
}

function usTin(s: string): Read | null {
  const c = idCompact(s);
  if (c.length !== 9 || !allDigits(c)) {
    return null;
  }
  const t = s.replace(SPACES_ALL, "");
  if (t[2] === "-") {
    return withKind(usEin(c), "ein");
  }
  if (t[3] === "-" || t[6] === "-") {
    return checked(ssnOk(c), c, `${c.slice(0, 3)}-${c.slice(3, 5)}-${c.slice(5)}`, "ssn");
  }
  return checked(einPrefix(c) || ssnOk(c), c, c);
}

function usZip(s: string): Read | null {
  const c = idCompact(s);
  if ((c.length !== 5 && c.length !== 9) || !allDigits(c)) {
    return null;
  }
  return checked(c.slice(0, 5) !== "00000", c, c.length === 9 ? `${c.slice(0, 5)}-${c.slice(5)}` : c);
}

function usRoutingNumber(s: string): Read | null {
  const c = idCompact(s);
  if (c.length !== 9 || !allDigits(c)) {
    return null;
  }
  const v = digitsOf(c);
  const district = (v[0] ?? 0) * 10 + (v[1] ?? 0);
  const inDistrict =
    district <= 12 || (district >= 21 && district <= 32) || (district >= 61 && district <= 72) || district === 80;
  return checked(weighted(v, [3, 7, 1, 3, 7, 1, 3, 7, 1]) % 10 === 0 && inDistrict && c !== "000000000", c, c);
}

const PROGRAM = /^[0-9]{9}[A-Z]{2}[0-9]{4}$/u;
const QST = /^[0-9]{10}TQ[0-9]{4}$/u;

function caBn(s: string): Read | null {
  const c = idCompact(s);
  const program = PROGRAM.test(c);
  if (!(program || (c.length === 9 && allDigits(c)))) {
    return null;
  }
  return checked(luhn(c.slice(0, 9)), c, program ? `${c.slice(0, 9)} ${c.slice(9)}` : c);
}

function caQst(s: string): Read | null {
  const c = idCompact(s);
  const full = QST.test(c);
  if (!(full || (c.length === 10 && allDigits(c)))) {
    return null;
  }
  return checked(full, c, full ? `${c.slice(0, 10)} ${c.slice(10)}` : c);
}

function auAbn(s: string): Read | null {
  const c = idCompact(s);
  if (c.length !== 11 || !allDigits(c)) {
    return null;
  }
  const v = digitsOf(c);
  const first = v[0] ?? 0;
  const valid = first > 0 && weighted([first - 1, ...v.slice(1)], [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]) % 89 === 0;
  return checked(valid, c, grouped(c, [2, 3, 3, 3], " "));
}

function auAcn(s: string): Read | null {
  const c = idCompact(s);
  if (c.length !== 9 || !allDigits(c)) {
    return null;
  }
  const v = digitsOf(c);
  return checked(
    (10 - (weighted(v.slice(0, 8), [8, 7, 6, 5, 4, 3, 2, 1]) % 10)) % 10 === v[8],
    c,
    grouped(c, [3, 3, 3], " "),
  );
}

function nzIrd(s: string): Read | null {
  const c = idCompact(s);
  if ((c.length !== 8 && c.length !== 9) || !allDigits(c)) {
    return null;
  }
  const k = c.padStart(9, "0");
  const v = digitsOf(k);
  const digit = (w: readonly number[]) => {
    const r = weighted(v.slice(0, 8), w) % 11;
    return r === 0 ? 0 : 11 - r;
  };
  const first = digit([3, 2, 7, 6, 5, 4, 3, 2]);
  const check = first === 10 ? digit([7, 4, 3, 2, 5, 2, 7, 6]) : first;
  const n = Number(k);
  const shown = k.replace(LEADING_ZEROS, "");
  const display = shown.length <= 8 ? grouped(shown.padStart(8, "0"), [2, 3, 3], "-") : grouped(k, [3, 3, 3], "-");
  return checked(n >= 10_000_000 && n <= 150_000_000 && check === v[8], k, display);
}

// ---------------------------------------------------------------------------------------------------------------
// Vehicles, devices, trade, versions (codes.rs).

const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
const VIN_SHAPE = /^[0-9A-Z]{17}$/u;
const VIN_BANNED = /[IOQ]/u;

function vinValue(ch: string): number {
  if (DIGIT.test(ch)) {
    return Number(ch);
  }
  const c = ch.charCodeAt(0);
  if (ch >= "A" && ch <= "H") {
    return c - 64;
  }
  if (ch >= "J" && ch <= "N") {
    return c - 73;
  }
  return ch === "P" ? 7 : ch === "R" ? 9 : Math.max(0, c - 83) + 2;
}

function vin(s: string, region: string | null | undefined): Read | null {
  const c = codeCompact(s);
  if (!VIN_SHAPE.test(c)) {
    return null;
  }
  const north = region === "US" || region === "CA";
  const sum = [...c].reduce((acc, ch, i) => acc + vinValue(ch) * (VIN_WEIGHTS[i] ?? 0), 0);
  const d = sum % 11;
  const digitOk = !north || c[8] === (d === 10 ? "X" : String(d));
  return checked(!VIN_BANNED.test(c) && digitOk, c, c);
}

function imei(s: string): Read | null {
  const c = codeCompact(s.replaceAll("/", ""));
  if (!allDigits(c)) {
    return null;
  }
  if (c.length === 15) {
    return checked(luhn(c), c, c, "imei");
  }
  if (c.length === 16) {
    return ok(c, c, "imeisv");
  }
  return c.length === 14 || c.length === 17 ? bad(c, c) : null;
}

function microchip(s: string): Read | null {
  const c = codeCompact(s);
  if (!allDigits(c) || c.length < 9 || c.length > 16) {
    return null;
  }
  return checked(c.length === 15 && c.slice(0, 3) !== "000" && c.slice(0, 3) !== "999", c, c);
}

function hsCode(s: string): Read | null {
  const c = codeCompact(s);
  if (!allDigits(c) || c.length < 4 || c.length > 12) {
    return null;
  }
  return checked([6, 8, 10].includes(c.length), c, grouped(c, [4, 2, 2, 2], "."));
}

const SEMVER =
  /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)(?:-((?:0|[1-9][0-9]*|[0-9]*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9][0-9]*|[0-9]*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+[0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*)?$/u;
const LEADS_DIGIT = /^[0-9]/u;
const pad10 = (n: string): string => n.padStart(10, "0");

function semver(s: string): Read | null {
  const t = s.startsWith("v") || s.startsWith("V") ? s.slice(1) : s;
  if (!(LEADS_DIGIT.test(t) && t.includes(".")) || SPACE.test(t)) {
    return null;
  }
  const m = t.match(SEMVER);
  if (!m) {
    return bad(t, t);
  }
  const [, major = "", minor = "", patch = "", pre] = m;
  const ids = pre?.split(".").map((id) => (allDigits(id) ? pad10(id) : id));
  return ok(`${pad10(major)}.${pad10(minor)}.${pad10(patch)}${ids ? `-${ids.join(".")}` : "~"}`, t);
}

// ---------------------------------------------------------------------------------------------------------------
// Locations (location.rs): `{lat, lng}`, typed as text.

/** A point: decimal degrees. */
export interface Point {
  lat: number;
  lng: number;
}

/** To 6 decimals (about 11 cm), as compared and shown. */
export const roundCoord = (x: number): number => {
  const r = Math.round(x * 1e6) / 1e6;
  return r === 0 ? 0 : r;
};
const coordText = (x: number): string => {
  const s = roundCoord(x).toFixed(6).replace(TRAILING_ZEROS, "").replace(TRAILING_DOT, "");
  return s === "-0" ? "0" : s;
};
const COORD_SPLIT = /[°º'"′″\sA-Za-z]+/u;
const ASCII_LETTERS = /[A-Za-z]/gu;
const LAT_LETTER = /[NS]/u;

function coordinate(s: string, pos: string, neg: string): number | null {
  const upper = s.trim().toUpperCase();
  const letters = upper.match(ASCII_LETTERS) ?? [];
  if (letters.length > 1) {
    return null;
  }
  const letter = letters[0];
  const sign = letter === undefined ? null : pos.includes(letter) ? 1 : neg.includes(letter) ? -1 : Number.NaN;
  if (Number.isNaN(sign)) {
    return null;
  }
  const parts = upper.split(COORD_SPLIT).filter(Boolean).map(Number);
  if (parts.length === 0 || parts.length > 3 || parts.some((x) => !Number.isFinite(x))) {
    return null;
  }
  const [first = 0, min = 0, sec = 0] = parts;
  const rest = parts.slice(1);
  const dmsOk =
    rest.every((x) => x >= 0 && x < 60) &&
    (rest.length === 0 || Number.isInteger(first)) &&
    (rest.length < 2 || Number.isInteger(min));
  const negative = first < 0 || Object.is(first, -0);
  if (!dmsOk || (sign !== null && negative)) {
    return null;
  }
  const mag = Math.abs(first) + min / 60 + sec / 3600;
  return sign === null ? (negative ? -mag : mag) : sign * mag;
}

/** Reads a location typed as text: `lat, lng` (or `;`, or a space between two numbers; hemisphere letters or signs) or DMS. */
export function parseLocation(value: string): Point | null {
  const s = value.trim();
  if (s === "" || s.length > 100) {
    return null;
  }
  let a: string;
  let b: string;
  const sep = s.search(LIST_SEPARATOR);
  if (sep >= 0) {
    [a, b] = [s.slice(0, sep), s.slice(sep + 1)];
  } else {
    const upper = s.toUpperCase();
    const i = upper.search(LAT_LETTER);
    if (i >= 0) {
      [a, b] = [upper.slice(0, i + 1), upper.slice(i + 1)];
    } else {
      const words = s.split(SPACES).filter(Boolean);
      if (words.length !== 2) {
        return null;
      }
      [a = "", b = ""] = words;
    }
  }
  const lat = coordinate(a, "N", "S");
  const lng = coordinate(b, "EL", "WO");
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng };
}

/** A stored location value's point (an object with numeric, in-range `lat` and `lng`). */
export function pointOf(v: unknown): (Point & { label?: string }) | null {
  if (typeof v !== "object" || v === null) {
    return null;
  }
  const o = v as Record<string, unknown>;
  const { lat, lng, label } = o;
  if (typeof lat !== "number" || typeof lng !== "number" || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng, ...(typeof label === "string" && { label }) };
}

/** `-23.5505, -46.6333`. */
export const showLocation = (p: Point): string => `${coordText(p.lat)}, ${coordText(p.lng)}`;

/** Its place on OpenStreetMap. */
export function mapUrl(p: Point): string {
  const [lat, lng] = [coordText(p.lat), coordText(p.lng)];
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
}

// ---------------------------------------------------------------------------------------------------------------

/** Longer values aren't read (no format's is near this). */
const MAX_LEN = 200;

const READERS: Readonly<Record<Kind, (s: string, region: string | null | undefined) => Read | null>> = {
  email,
  phone,
  url,
  br_cpf: cpf,
  br_cnpj: cnpj,
  br_cep: cep,
  br_pix_key: pix,
  license_plate: plate,
  iban,
  bic,
  ean,
  isbn,
  uuid,
  ip,
  cidr,
  mac,
  domain,
  br_ie: ie,
  br_ncm: ncm,
  br_cest: cest,
  br_cfop: cfop,
  br_nfe_key: nfeKey,
  br_boleto: boleto,
  br_pis: pis,
  br_renavam: renavam,
  br_bank_branch: bankBranch,
  br_bank_account: bankAccount,
  it_codice_fiscale: codiceFiscale,
  it_partita_iva: partitaIva,
  pt_nif: ptNif,
  es_nif: esNif,
  fr_siren: siren,
  fr_siret: siret,
  fr_tva: frTva,
  de_steuernummer: steuernummer,
  de_ust_idnr: ustIdnr,
  de_leitweg_id: leitwegId,
  eu_vat: euVat,
  uk_vat: ukVat,
  uk_company_number: ukCompanyNumber,
  uk_utr: ukUtr,
  uk_postcode: ukPostcode,
  uk_sort_code: ukSortCode,
  us_ein: usEin,
  us_tin: usTin,
  us_zip: usZip,
  us_routing_number: usRoutingNumber,
  ca_bn: caBn,
  ca_qst: caQst,
  au_abn: auAbn,
  au_acn: auAcn,
  nz_ird: nzIrd,
  vin,
  imei,
  microchip,
  hs_code: hsCode,
  semver,
  // Without the field's spec, only its key: `Custom.read` checks and shows it.
  custom: (s) => {
    const k = customKey(s);
    return k ? ok(k, s) : null;
  },
};

/** Reads `s` (trimmed) as `kind` in `region`; null when it isn't shaped like one at all. */
export function read(kind: Kind, value: string, region?: string | null): Read | null {
  const s = value.trim();
  if (s === "" || new TextEncoder().encode(s).length > MAX_LEN) {
    return null;
  }
  return READERS[kind](s, region);
}

/** What `s` compares by: its key, else a loose form (a phone's `+` and digits; anything else trimmed, lowercased). */
export function keyOf(kind: Kind, value: string, region?: string | null): string {
  const r = read(kind, value, region);
  if (r) {
    return r.key;
  }
  return kind === "phone" ? value.replace(NOT_PHONE, "") : value.trim().toLowerCase();
}

/** How a value fails its kind: `international`, a strict phone field's value not written `+…`. */
export type Check = "valid" | "invalid" | "international";

export function checkOf(kind: Kind, value: string, region?: string | null, strict = false): Check {
  if (strict && kind === "phone" && !value.trimStart().startsWith("+")) {
    return "international";
  }
  return read(kind, value, region)?.valid ? "valid" : "invalid";
}

/** A field's own pattern and mask, as fields carry them. */
interface Own {
  pattern?: string | null;
  mask?: string | null;
}

/** Reads a value as its field does: by `kind` in `region`, or (`custom`) by the field's own pattern and mask. */
export function readField(own: Own, kind: Kind, value: string, region?: string | null): Read | null {
  if (kind !== "custom") {
    return read(kind, value, region);
  }
  const spec = customOf(own);
  return spec && value.trim() !== "" ? spec.read(value) : null;
}

/** How a value shows: its display form when it's valid, else as written. */
export function shownOf(kind: Kind, value: string, region?: string | null): string {
  const r = read(kind, value, region);
  return r?.valid ? r.display : value;
}

// ---------------------------------------------------------------------------------------------------------------
// Typing them: placeholders and masks.

/** An example of each format (a placeholder); phones' depend on the region. */
const EXAMPLES: Partial<Record<Kind, string>> = {
  email: "ana@example.com",
  url: "example.com",
  br_cpf: "000.000.000-00",
  br_cnpj: "AA.AAA.AAA/AAAA-00",
  br_cep: "00000-000",
  iban: "DE89 3704 0044 0532 0130 00",
  bic: "DEUTDEFF",
  ean: "4006381333931",
  isbn: "978-0-306-40615-7",
  uuid: "550e8400-e29b-41d4-a716-446655440000",
  ip: "192.168.0.1",
  cidr: "10.0.0.0/8",
  mac: "aa:bb:cc:dd:ee:ff",
  domain: "example.com",
  br_ie: "000.000.000.000",
  br_ncm: "0000.00.00",
  br_cest: "00.000.00",
  br_cfop: "0.000",
  br_nfe_key: "0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000",
  br_boleto: "00000.00000 00000.000000 00000.000000 0 00000000000000",
  br_pis: "000.00000.00-0",
  br_renavam: "00000000000",
  br_bank_branch: "0000-0",
  br_bank_account: "00000-0",
  it_codice_fiscale: "RSSMRA85T10A562S",
  it_partita_iva: "07643520567",
  pt_nif: "501964843",
  es_nif: "12345678Z",
  fr_siren: "732 829 320",
  fr_siret: "732 829 320 00074",
  fr_tva: "FR 40 303 265 045",
  de_steuernummer: "93815/08152",
  de_ust_idnr: "DE136695976",
  de_leitweg_id: "04011000-1234512345-06",
  eu_vat: "IT07643520567",
  uk_vat: "GB 980 7806 84",
  uk_company_number: "SC123456",
  uk_utr: "19558 39661",
  uk_postcode: "SW1A 1AA",
  uk_sort_code: "00-00-00",
  us_ein: "00-0000000",
  us_tin: "00-0000000",
  us_zip: "00000",
  us_routing_number: "021000021",
  ca_bn: "123456782 RT0001",
  ca_qst: "1234567890 TQ0001",
  au_abn: "51 824 753 556",
  au_acn: "004 085 616",
  nz_ird: "49-091-850",
  vin: "1M8GDM9AXKP042788",
  imei: "490154203237518",
  microchip: "985112000123456",
  hs_code: "8471.30",
  semver: "1.2.3",
};

/** The plates' examples, by country. */
const PLATE_EXAMPLES: Readonly<Record<string, string>> = {
  BR: "ABC1D23",
  AR: "AB 123 CD",
  IT: "AB 123 CD",
  PT: "AA-00-AA",
  GB: "AB12 CDE",
  IE: "191-D-12345",
  DE: "M-AB 1234",
  FR: "AB-123-CD",
  ES: "1234 BCD",
};

/** A placeholder for a field's values: its kind's example, or its own mask. */
export const placeholderOf = (own: Own, kind: Kind, region?: string | null): string | undefined =>
  kind === "custom" ? customOf(own)?.placeholder : placeholder(kind, region);

/** What typing `s` shows in a field: its kind's mask, or its own. */
export const maskOf = (own: Own, kind: Kind, s: string): string =>
  kind === "custom" ? (customOf(own)?.typed(s) ?? s) : mask(kind, s);

/** A placeholder for a value of `kind` in `region`. */
export function placeholder(kind: Kind, region?: string | null): string | undefined {
  if (kind === "phone") {
    return phoneExample(region);
  }
  if (kind === "license_plate") {
    return region ? PLATE_EXAMPLES[canonicalRegion(region)] : undefined;
  }
  return EXAMPLES[kind];
}

/** Masks: `0` a digit, `A` a letter or digit, anything else a separator put in as typing reaches it. */
const MASKS: Partial<Record<Kind, string>> = {
  br_cpf: "000.000.000-00",
  br_cnpj: "AA.AAA.AAA/AAAA-00",
  br_cep: "00000-000",
  br_ncm: "0000.00.00",
  br_cest: "00.000.00",
  br_cfop: "0.000",
  br_pis: "000.00000.00-0",
  br_nfe_key: "0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000",
  fr_siren: "000 000 000",
  fr_siret: "000 000 000 00000",
  uk_sort_code: "00-00-00",
  uk_utr: "00000 00000",
  us_ein: "00-0000000",
  au_abn: "00 000 000 000",
  au_acn: "000 000 000",
  hs_code: "0000.00.00.00",
};

/**
 * What typing `s` shows with `kind`'s mask: its digits (letters too where the mask takes them) with the separators put
 * in. Unchanged when it has anything else, or more than the mask takes (it's left as written, to be reported).
 */
export function mask(kind: Kind, s: string): string {
  const m = MASKS[kind];
  if (kind === "iban") {
    const c = s.replace(/ /gu, "").toUpperCase();
    return ALNUM_OR_EMPTY.test(c) && c.length <= 34 ? groups(c, 4) : s;
  }
  if (!m) {
    return s;
  }
  const raw = s.replace(BR_SEPARATORS, "").toUpperCase();
  const slots = [...m].filter((ch) => ch === "0" || ch === "A").length;
  if (raw.length > slots || !ALNUM_OR_EMPTY.test(raw)) {
    return s;
  }
  let out = "";
  let i = 0;
  for (const ch of m) {
    if (i >= raw.length) {
      break;
    }
    const next = raw[i] ?? "";
    if (ch === "0" || ch === "A") {
      if (ch === "0" && !DIGIT.test(next)) {
        return s;
      }
      out += next;
      i += 1;
    } else {
      out += ch;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Quantities' units (server/src/formats/units.rs).

/** (code, dimension), by dimension. */
/** What a unit measures. */
type Dimension = "mass" | "length" | "area" | "volume" | "time" | "count" | "data";

export const UNITS: readonly (readonly [string, Dimension])[] = [
  ["mg", "mass"],
  ["g", "mass"],
  ["kg", "mass"],
  ["t", "mass"],
  ["oz", "mass"],
  ["lb", "mass"],
  ["mm", "length"],
  ["cm", "length"],
  ["m", "length"],
  ["km", "length"],
  ["in", "length"],
  ["ft", "length"],
  ["yd", "length"],
  ["mi", "length"],
  ["cm2", "area"],
  ["m2", "area"],
  ["km2", "area"],
  ["ha", "area"],
  ["ft2", "area"],
  ["ac", "area"],
  ["ml", "volume"],
  ["cl", "volume"],
  ["l", "volume"],
  ["m3", "volume"],
  ["fl_oz", "volume"],
  ["gal", "volume"],
  ["s", "time"],
  ["min", "time"],
  ["h", "time"],
  ["d", "time"],
  ["wk", "time"],
  ["mo", "time"],
  ["yr", "time"],
  ["pc", "count"],
  ["pair", "count"],
  ["dozen", "count"],
  ["box", "count"],
  ["B", "data"],
  ["kB", "data"],
  ["MB", "data"],
  ["GB", "data"],
  ["TB", "data"],
];

/** Units written the same in every language, by their symbol where it isn't the code. */
const SYMBOLS: Readonly<Record<string, string>> = {
  cm2: "cm²",
  m2: "m²",
  km2: "km²",
  ft2: "ft²",
  m3: "m³",
  fl_oz: "fl oz",
};

/** Units whose symbol is a word in each language (an i18n key `unit.<code>`). */
const WORDS: ReadonlySet<string> = new Set(["d", "wk", "mo", "yr", "pc", "pair", "dozen", "box"]);

/** A unit's symbol: localized where it's a word, else its own; an unknown code as it is. */
export const unitSymbol = (code: string): string =>
  WORDS.has(code) ? translate(`unit.${code}` as Key) : (SYMBOLS[code] ?? code);
